/**
 * Small inline badge that colorizes a user's status.
 *
 * Color mapping is warm-only, matching the palette rules:
 *   - active → honey (warm gold — approved, in good standing)
 *   - pending_approval → amber (awaiting review)
 *   - disabled → coral-deep (closed window)
 *   - super admin (optional) → honey-deep border (special)
 *
 * RWAR Red is NOT used here per STYLE_GUIDE §2.1 — red is reserved
 * for the pin indicator and the active-tab underline. Admin status
 * badges use the warm accent family.
 */

import type { UserStatus } from "@/features/auth/useAuth";

interface Props {
  status: UserStatus;
}

const STATUS_COLORS: Record<
  UserStatus,
  { bg: string; text: string; border: string; label: string }
> = {
  active: {
    bg: "bg-honey-soft",
    text: "text-honey-deep",
    border: "border-honey-deep/40",
    label: "Active",
  },
  pending_approval: {
    bg: "bg-amber-soft",
    text: "text-amber-deep",
    border: "border-amber-deep/40",
    label: "Pending",
  },
  disabled: {
    bg: "bg-coral-soft",
    text: "text-coral-deep",
    border: "border-coral-deep/40",
    label: "Disabled",
  },
};

export function StatusBadge({ status }: Props) {
  const cfg = STATUS_COLORS[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-[2px] rounded-pill border text-[11px] font-sans font-semibold uppercase tracking-[0.08em] ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.label}
    </span>
  );
}
